/**
 * Project open/create dialog.
 */

import { Projects, type LoadedProject, type ProjectSummary } from '../session.ts';
import { button, buttons, errorLine, field, openDialog } from './dialog.ts';

export function showProjectDialog(
  onOpen: (project: LoadedProject) => void,
  onFromDisk?: () => void,
): void {
  openDialog({
    title: 'Projects',
    build(body, handle) {
      const err = errorLine(body);

      const list = document.createElement('div');
      list.className = 'proj-list';
      list.textContent = 'Loading…';
      body.append(list);

      const form = document.createElement('form');
      form.className = 'modal-form new-project';
      body.append(form);

      const name = field(form, 'New project', 'text', {
        placeholder: 'e.g. Preamp rev B',
      });

      const row = buttons(form);
      // Open has to lead somewhere for a file that is not a project yet.
      // Without this it lists stored projects and stops, which reads as the
      // program refusing to open files at all.
      if (onFromDisk) {
        button(row, 'From this computer…', () => { handle.close(); onFromDisk(); });
      }
      button(row, 'Cancel', () => handle.close());
      const create = button(row, 'Create', () => form.requestSubmit(), true);

      form.addEventListener('submit', (ev) => {
        ev.preventDefault();
        const value = name.value.trim();
        if (!value) { fail('Give the project a name.'); return; }

        create.disabled = true;
        create.textContent = 'Creating…';
        Projects.create(value)
          .then((p) => { handle.close(); onOpen(p); })
          .catch((e: Error) => {
            fail(e.message);
            create.disabled = false;
            create.textContent = 'Create';
          });
      });

      Projects.list()
        .then((rows) => renderList(rows))
        .catch((e: Error) => {
          list.textContent = '';
          fail(e.message);
        });

      function renderList(rows: ProjectSummary[]): void {
        list.replaceChildren();
        if (rows.length === 0) {
          const p = document.createElement('p');
          p.className = 'modal-hint';
          p.textContent = 'No projects yet. Create one below.';
          list.append(p);
          return;
        }

        for (const r of rows) {
          const item = document.createElement('div');
          item.className = 'proj';

          const main = document.createElement('div');
          main.className = 'proj-main';

          const nm = document.createElement('span');
          nm.className = 'proj-name';
          nm.textContent = r.name;

          const meta = document.createElement('span');
          meta.className = 'proj-meta';
          meta.textContent =
            `${r.documents} document${r.documents === 1 ? '' : 's'} · ${r.updated_at}`;

          main.append(nm, meta);
          item.append(main);

          const open = document.createElement('button');
          open.type = 'button';
          open.className = 'btn';
          open.textContent = 'Open';
          open.addEventListener('click', () => {
            open.disabled = true;
            open.textContent = 'Opening…';
            Projects.open(r.id)
              .then((p) => { handle.close(); onOpen(p); })
              .catch((e: Error) => {
                fail(e.message);
                open.disabled = false;
                open.textContent = 'Open';
              });
          });

          const del = document.createElement('button');
          del.type = 'button';
          del.className = 'btn danger';
          del.textContent = 'Delete';
          del.addEventListener('click', () => {
            // Deleting a project takes its documents with it, so confirm.
            if (!window.confirm(
              `Delete "${r.name}" and all of its documents? This cannot be undone.`)) {
              return;
            }
            Projects.remove(r.id)
              .then(() => Projects.list())
              .then(renderList)
              .catch((e: Error) => fail(e.message));
          });

          item.append(open, del);
          list.append(item);
        }
      }

      function fail(message: string): void {
        err.textContent = message;
        err.hidden = false;
      }
    },
  });
}
